import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// The linter doesn't know that the eventListeners have been bound.  Turn off the rule for this file.
/* eslint-disable @typescript-eslint/unbound-method */

import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorContext } from "abweb/utils/error/ErrorContext";
import { EventListenerManager } from "abweb/utils/EventRegistration";
import { AlreadyHandledError } from "abweb/utils/PromiseUtils";

/**
 * This manages registering raw event listeners for uncaught errors throws in Tasks,
 * and for uncaught errors thrown in in micro-tasks (promise chains).
 * It will silently ignore errors that are instances of the 'AlreadyHandled' error class.
 * For the remaining errors that need to be processed,
 * it will call the provided errorEventHandler and promiseRejectionEventHandler.
 * There is additional logic to ensure that the
 * promiseRejectionEventHandler will only be fired once the error is considered 'firm' (unlikely to be retracted).
 *
 * It converts all incoming error events into corresponding ErrorContext instances, and then
 * ultimately invokes the errorContextHandler (pipeline) with the ErrorContext instance.
 *
 * To unregister the event listeners, call destroy on the ErrorDispatcher instance.
 */
export class ErrorDispatcher {
  constructor(errContextHandler) {
    _defineProperty(this, "pendingPromiseEvents", []);
    // @ts-ignore: noUnusedLocals
    _defineProperty(this, "numPendingTimeouts", 0);
    // only used by tests to wait for timeouts to fire before checking
    _defineProperty(this, "errorContextHandler", void 0);
    _defineProperty(this, "eventListeners", void 0);
    // Build pipleline:  dispatcher -> resolver -> consoleLogger (-> errorRender)
    this.errorContextHandler = errContextHandler;
    this.eventListeners = new EventListenerManager();
    this.registerErrorListeners();
  }
  destroy() {
    this.eventListeners.unregister();
  }
  registerErrorListeners() {
    this.eventListeners.registerEventListener(window, "error", this.recordError.bind(this));
    // This registers listener for unhandled promise exceptions.
    this.eventListeners.registerEventListener(window, "unhandledrejection", this.recordUnhandledRejection.bind(this));
    this.eventListeners.registerEventListener(window, "rejectionhandled", this.recordRejectionHandled.bind(this));
  }
  recordError(errEv) {
    if (this.skipError(errEv.error, errEv.message)) {
      return;
    }
    // CAT-842: ensure downstream consumers deal with strongly-typed Error instance
    const errCtx = ErrorContext.createFromErrorEvent(errEv);
    this.errorContextHandler.handleErrorContext(errCtx);
  }

  // Promise Rejection Events can be retracted by a subsequent rejectionHandled event.  As such, only treat it as an error
  // if we're sufficiently sure that isn't going to be retracted (once the error is 'firm')
  // See Timeout comments below, and UnhandledRejectionPolyfillUSpec tests:
  // "pathological reject w/eventual (after delay) catch" and "reject w/await catch"
  recordUnhandledRejection(e) {
    // If error is 'already handled', then do nothing.
    // It is the responsibility of the original handler to log the/ error (if needed)
    if (this.skipError(e.reason, e.message)) {
      return;
    }

    // defensive assert
    if (e.reason == null) {
      console.error("Received unhandledrejection event with null reason.  Should never happen, skipping raw event: " + e.toString());
      return;
    }
    console.debug("Received unhandledrejection: " + e.reason.toString());
    this.pendingPromiseEvents.push(e);
    // defensive check...
    if (this.numPendingEvents() > 10) {
      console.warn("Promise error queue is getting unexpectedly long: " + this.numPendingEvents());
    }
    this.numPendingTimeouts++;

    // Experiment with queuing up a new TASK (not microtask) to only consume error if it hasn't been retracted (believe this
    // will handle async functions with try/catch, where polyfill browsers fire an unhandledrejection (try block) followed by
    // a retraction (rejectionhandled) when it gets to the catch block.  This works as 'non-async microtasks' (like
    // programmatically adding a catch via 'p.catch....') are guaranteed to drain before next task is executed.
    setTimeout(() => {
      this.numPendingTimeouts--;
      const pre = this.findAndRemoveEvent(e);
      if (pre) {
        // If error still exists, then it hasn't been retracted, so fire as it is now 'firm'
        console.debug("Firing firm promise error: " + e.reason.toString());
        // CAT-842: ensure downstream consumers deal with strongly-typed Error instance
        const errCtx = ErrorContext.createFromPromiseRejectionEvent(pre);
        this.errorContextHandler.handleErrorContext(errCtx);
      } else {
        console.debug("Cancelled firing firm promise error as retracted from pending list: " + e.reason.toString());
      }
    }, 0);
  }

  // Also used by testing
  numPendingEvents() {
    return this.pendingPromiseEvents.length;
  }
  recordRejectionHandled(e) {
    if (this.skipError(e.reason, e.message)) {
      return;
    }
    console.debug("Retracting unhandledrejection: " + e.reason.toString());
    const pre = this.findAndRemoveEquivalentEvent(e);
    if (!pre) {
      // Don't expect this to happen (means they manually added a 'p.catch' way after the initial p promise chain
      // ran, which is an anti-pattern.)  Log it as a warning.
      // See UnhandledRejectionPolyfillUSpec:"pathological reject w/eventual (after delay) catch" for more info
      console.warn("The retracted error was not in pendingError list, probably because of a delayed catch: " + e.reason.toString());
    }
  }
  skipError(err, msg) {
    return ClassUtils.isInstanceOf(err, AlreadyHandledError); // returns false if err is null
  }
  findAndRemoveEvent(e) {
    const errIndex = this.pendingPromiseEvents.indexOf(e);
    if (errIndex < 0) {
      return null;
    }
    const deletedItems = this.pendingPromiseEvents.splice(errIndex, 1);
    return deletedItems[0];
  }

  // Rejection events are different event instances with same promise and reason as original unhandledrejection event
  findAndRemoveEquivalentEvent(e) {
    let i = 0;
    let errIndex = -1;
    for (let ev of this.pendingPromiseEvents) {
      if (e.promise === ev.promise && e.reason === ev.reason) {
        errIndex = i;
        break;
      }
      i++;
    }
    if (errIndex < 0) {
      return null;
    }
    const deletedItems = this.pendingPromiseEvents.splice(errIndex, 1);
    return deletedItems[0];
  }
}
//# sourceMappingURL=ErrorDispatcher.js.map
